import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import { cx } from './cx'
import { Icon } from './Icon'
import { FieldShell } from './FieldShell'
import { SelectField } from './SelectField'
import { DateField, isDateKind } from './DateField'

const ALWAYS_FLOATED_TYPES = new Set(['date', 'time', 'datetime-local', 'month', 'week', 'file', 'color'])

interface SharedFieldProps {
  label: string
  variant?: 'outlined' | 'filled' | undefined
  supportingText?: ReactNode | undefined
  /** Messaggio di errore: imposta aria-invalid e sostituisce il supporting text. */
  error?: string | undefined
  /** Nasconde la label alla vista (resta per gli screen reader) e disattiva l'effetto flottante. */
  labelHidden?: boolean | undefined
  leadingIcon?: string | undefined
  trailingIcon?: string | undefined
}

export interface TextFieldProps
  extends SharedFieldProps,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'size' | 'children'> {
  prefix?: ReactNode | undefined
  suffix?: ReactNode | undefined
  multiline?: boolean | undefined
  rows?: number | undefined
  /** Mostra il testo in maiuscolo (solo resa: il valore inviato non cambia). Sigle, IBAN, codici. */
  uppercase?: boolean | undefined
}

export const TextField = forwardRef<HTMLInputElement | HTMLTextAreaElement, TextFieldProps>(function TextField(
  {
    label,
    variant = 'outlined',
    supportingText,
    error,
    leadingIcon,
    trailingIcon,
    prefix,
    suffix,
    multiline,
    rows = 3,
    uppercase,
    labelHidden,
    id: idProp,
    className,
    disabled,
    type,
    placeholder,
    'aria-describedby': ariaDescribedBy,
    ...rest
  },
  ref,
) {
  const autoId = useId()
  const id = idProp ?? autoId
  const msgId = `${id}-msg`
  const hasMessage = Boolean(error || supportingText)
  const describedBy = [ariaDescribedBy, hasMessage ? msgId : undefined].filter(Boolean).join(' ') || undefined
  const hasPlaceholder = Boolean(placeholder && placeholder.trim())
  if (!multiline && isDateKind(type)) {
    return (
      <DateField
        {...(rest as object)}
        ref={ref as Ref<HTMLInputElement>}
        kind={type}
        label={label}
        variant={variant}
        supportingText={supportingText}
        error={error}
        leadingIcon={leadingIcon}
        labelHidden={labelHidden}
        id={idProp}
        className={className}
        disabled={disabled}
        placeholder={placeholder}
        aria-describedby={ariaDescribedBy}
      />
    )
  }
  const floated = Boolean(prefix || suffix || hasPlaceholder || (type && ALWAYS_FLOATED_TYPES.has(type)))
  // Placeholder reale se passato (la label resta sollevata); altrimenti " " serve a :placeholder-shown.
  const ph = hasPlaceholder ? placeholder : labelHidden ? placeholder : ' '
  const common = {
    id,
    disabled,
    placeholder: ph,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy,
    className: cx('md-field__input', uppercase && 'md-field__input--uppercase'),
  }
  return (
    <FieldShell
      id={id}
      label={label}
      labelHidden={labelHidden}
      variant={variant}
      supportingText={supportingText}
      error={error}
      disabled={disabled}
      floated={floated}
      leadingIcon={leadingIcon}
      trailingIcon={trailingIcon}
      prefix={prefix}
      suffix={suffix}
      multiline={Boolean(multiline)}
      className={className}
      describedBy={hasMessage ? msgId : undefined}
    >
      {multiline ? (
        <textarea
          ref={ref as Ref<HTMLTextAreaElement>}
          rows={rows}
          {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)}
          {...common}
        />
      ) : (
        <input ref={ref as Ref<HTMLInputElement>} type={type} {...rest} {...common} />
      )}
    </FieldShell>
  )
})

export interface SelectProps extends SharedFieldProps, Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  children: ReactNode
}

/**
 * Select M3 (exposed dropdown menu). Con `multiple`/`size` resta la lista nativa: li' non c'e' un menu a tendina.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(props, ref) {
  if (props.multiple || (props.size ?? 0) > 1) return <NativeSelect {...props} ref={ref} />
  return <SelectField {...props} ref={ref} />
})

/** Select nativo con la stessa cornice di TextField (la label e' sempre sollevata). */
const NativeSelect = forwardRef<HTMLSelectElement, SelectProps>(function NativeSelect(
  {
    label,
    variant = 'outlined',
    supportingText,
    error,
    leadingIcon,
    labelHidden,
    id: idProp,
    className,
    disabled,
    children,
    'aria-describedby': ariaDescribedBy,
    ...rest
  },
  ref,
) {
  const autoId = useId()
  const id = idProp ?? autoId
  const msgId = `${id}-msg`
  const hasMessage = Boolean(error || supportingText)
  const describedBy = [ariaDescribedBy, hasMessage ? msgId : undefined].filter(Boolean).join(' ') || undefined
  return (
    <FieldShell
      id={id}
      label={label}
      labelHidden={labelHidden}
      variant={variant}
      supportingText={supportingText}
      error={error}
      disabled={disabled}
      floated
      leadingIcon={leadingIcon}
      trailingIcon="arrow_drop_down"
      prefix={undefined}
      suffix={undefined}
      multiline={false}
      className={cx('md-field--select', className)}
      describedBy={hasMessage ? msgId : undefined}
    >
      <select
        ref={ref}
        id={id}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        {...rest}
        className="md-field__input"
      >
        {children}
      </select>
    </FieldShell>
  )
})

interface ChoiceProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'children'> {
  label: ReactNode
}

export interface CheckboxProps extends ChoiceProps {
  indeterminate?: boolean | undefined
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, indeterminate, className, id: idProp, ...rest },
  ref,
) {
  const autoId = useId()
  const id = idProp ?? autoId
  const inner = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    if (inner.current) inner.current.indeterminate = Boolean(indeterminate)
  }, [indeterminate])
  return (
    <label htmlFor={id} className={cx('md-choice', rest.disabled && 'md-choice--disabled', className)}>
      <input
        {...rest}
        id={id}
        type="checkbox"
        ref={(el) => {
          inner.current = el
          if (typeof ref === 'function') ref(el)
          else if (ref) ref.current = el
        }}
        className="md-choice__input"
      />
      <span className="md-choice__box md-choice__box--check" aria-hidden="true">
        <Icon name="check" size={18} className="md-choice__check" />
        <Icon name="remove" size={18} className="md-choice__dash" />
      </span>
      <span className="md-choice__label">{label}</span>
    </label>
  )
})

export const Radio = forwardRef<HTMLInputElement, ChoiceProps>(function Radio({ label, className, id: idProp, ...rest }, ref) {
  const autoId = useId()
  const id = idProp ?? autoId
  return (
    <label htmlFor={id} className={cx('md-choice', rest.disabled && 'md-choice--disabled', className)}>
      <input {...rest} id={id} ref={ref} type="radio" className="md-choice__input" />
      <span className="md-choice__box md-choice__box--radio" aria-hidden="true" />
      <span className="md-choice__label">{label}</span>
    </label>
  )
})

export const Switch = forwardRef<HTMLInputElement, ChoiceProps>(function Switch({ label, className, id: idProp, ...rest }, ref) {
  const autoId = useId()
  const id = idProp ?? autoId
  return (
    <label htmlFor={id} className={cx('md-choice', rest.disabled && 'md-choice--disabled', className)}>
      <input {...rest} id={id} ref={ref} type="checkbox" role="switch" className="md-choice__input" />
      <span className="md-switch__track" aria-hidden="true">
        <span className="md-switch__thumb" />
      </span>
      <span className="md-choice__label">{label}</span>
    </label>
  )
})
